import { useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { updateBusinessTripSettings } from "services/company.service";
import { SettingsCard, StatusBanner, useSaveWithToast } from "./ui";
import { TemplateSlotRow } from "./template-slot-row";
import { useTemplateSlotNames } from "./use-template-slot-names";
import {
  SlotValues,
  readSlotValues,
  slotPayload,
  slotSaveErrorMessage,
} from "./template-slot-values";

// The documents a business trip produces, and the company fields that hold
// each one's settings.
const TRIP_DOCUMENTS = [
  {
    key: "main",
    labelKey: "business_trip_main_template",
    fields: {
      template: "businessTripMainTemplate",
      source: "businessTripMainTemplateSource",
      series: "businessTripMainSeries",
      fileType: "businessTripMainFileType",
      category: "businessTripMainCategory",
    },
  },
  {
    key: "report",
    labelKey: "business_trip_report_template",
    fields: {
      template: "businessTripReportTemplate",
      source: "businessTripReportTemplateSource",
      series: "businessTripReportSeries",
      fileType: "businessTripReportFileType",
      category: "businessTripReportCategory",
    },
  },
  {
    key: "leave",
    labelKey: "business_trip_leave_template",
    fields: {
      template: "businessTripLeaveTemplate",
      source: "businessTripLeaveTemplateSource",
      series: "businessTripLeaveSeries",
      fileType: "businessTripLeaveFileType",
      category: "businessTripLeaveCategory",
    },
  },
] as const;

type TripDocumentKey = typeof TRIP_DOCUMENTS[number]["key"];
// The values being edited per document; null while that document's row is closed.
type Drafts = Record<TripDocumentKey, SlotValues | null>;

const ALL_CLOSED: Drafts = { main: null, report: null, leave: null };

interface BusinessTripSettingsContainerProps {
  companyEntity: any;
  onRefresh: () => void;
}

export default function BusinessTripSettingsContainer({
  companyEntity,
  onRefresh,
}: BusinessTripSettingsContainerProps) {
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId: string }>();
  const names = useTemplateSlotNames(companyId);
  const { save, saving, error } = useSaveWithToast();

  // Several documents can be open at once and one Save stores them all: the
  // server takes the three documents in a single request and refuses it while
  // any of them still lacks its series, type or category, so a company that
  // has set up none of them yet has to fill them in together.
  const [drafts, setDrafts] = useState<Drafts>(ALL_CLOSED);

  const patchDraft = (key: TripDocumentKey, patch: Partial<SlotValues>) =>
    setDrafts((open) => {
      const current = open[key];
      return current ? { ...open, [key]: { ...current, ...patch } } : open;
    });

  const handleSave = async () => {
    const body = Object.assign(
      {},
      ...TRIP_DOCUMENTS.map((doc) =>
        slotPayload(
          doc.fields,
          drafts[doc.key] ?? readSlotValues(companyEntity, doc.fields)
        )
      )
    );
    const saved = await save(
      async () => {
        await updateBusinessTripSettings(body, companyId);
        onRefresh();
      },
      { errorMessage: (err) => slotSaveErrorMessage(err, t) }
    );
    if (saved) setDrafts(ALL_CLOSED);
  };

  // One Save covers every open row, so a failure is shown once, under the first open one.
  const firstOpenKey = TRIP_DOCUMENTS.find((doc) => drafts[doc.key])?.key;

  const usesCustomTemplate = TRIP_DOCUMENTS.some(
    (doc) => readSlotValues(companyEntity, doc.fields).source === "custom"
  );

  return (
    <div className="space-y-5">
      {usesCustomTemplate && (
        <StatusBanner
          tone="warn"
          title={t("business_trip_custom_template_warning")}
        />
      )}

      <SettingsCard>
        {TRIP_DOCUMENTS.map((doc) => {
          const savedValues = readSlotValues(companyEntity, doc.fields);
          return (
            <TemplateSlotRow
              key={doc.key}
              label={t(doc.labelKey)}
              saved={savedValues}
              names={names}
              draft={drafts[doc.key]}
              onEdit={() =>
                setDrafts((open) => ({ ...open, [doc.key]: savedValues }))
              }
              onDraftChange={(patch) => patchDraft(doc.key, patch)}
              onSave={handleSave}
              onCancel={() =>
                setDrafts((open) => ({ ...open, [doc.key]: null }))
              }
              saving={saving}
              error={doc.key === firstOpenKey ? error : null}
            />
          );
        })}
      </SettingsCard>
    </div>
  );
}

export function BusinessTripSettingsRedirect() {
  const { companyId } = useParams<{ companyId: string }>();
  return <Navigate to={`/app/${companyId}/settings/business-trips`} replace />;
}
